import "@fontsource/chakra-petch/500.css";
import "@fontsource/chakra-petch/600.css";
import "@fontsource/chakra-petch/700.css";
import "@fontsource-variable/instrument-sans";
import "@fontsource-variable/jetbrains-mono";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router";
import { AuthProvider, RequireAuth } from "./auth.tsx";
import { LiveProvider } from "./live.tsx";
import { UIProvider } from "./ui/index.tsx";
import { AppLayout, PublicLayout } from "./layouts.tsx";
import Landing from "./pages/Landing.tsx";
import Status from "./pages/Status.tsx";
import PublicGames from "./pages/Games.tsx";
import Product from "./pages/Product.tsx";
import LinkDiscord from "./pages/LinkDiscord.tsx";
import Checkout, { BundleCheckout } from "./pages/Checkout.tsx";
import CheckoutComplete from "./pages/CheckoutComplete.tsx";
import Pricing from "./pages/Pricing.tsx";
import Login from "./pages/Login.tsx";
import Register from "./pages/Register.tsx";
import Forgot from "./pages/Forgot.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import VerifyEmail from "./pages/VerifyEmail.tsx";
import Home from "./pages/Home.tsx";
import Licenses from "./pages/Licenses.tsx";
import Downloads from "./pages/Downloads.tsx";
import Guides, { GuideView } from "./pages/Guides.tsx";
import Support, { TicketView } from "./pages/Support.tsx";
import Account from "./pages/Account.tsx";
import NotFound from "./pages/NotFound.tsx";
import Changelog from "./pages/Changelog.tsx";
import { TitleSync } from "./title.tsx";
import { ErrorBoundary, installErrorReporting } from "./errors.tsx";
import { CommandPalette } from "./palette.tsx";
import Overview from "./pages/admin/Overview.tsx";
import Users from "./pages/admin/Users.tsx";
import Keys from "./pages/admin/Keys.tsx";
import AdminTickets from "./pages/admin/Tickets.tsx";
import Content from "./pages/admin/Content.tsx";
import Releases from "./pages/admin/Releases.tsx";
import Games from "./pages/admin/Games.tsx";
import Payments from "./pages/admin/Payments.tsx";
import Diagnostics from "./pages/admin/Diagnostics.tsx";
import Audit from "./pages/admin/Audit.tsx";
import Settings from "./pages/admin/Settings.tsx";
import Incidents from "./pages/admin/Incidents.tsx";

function App() {
  return (
    <>
    <TitleSync />
    <Routes>
      <Route element={<PublicLayout />}>
        <Route index element={<Landing />} />
        <Route path="games" element={<PublicGames />} />
        <Route path="games/:id" element={<Product />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="status" element={<Status />} />
        <Route path="changelog" element={<Changelog />} />
      </Route>
      <Route path="login" element={<Login />} />
      <Route path="register" element={<Register />} />
      <Route path="forgot" element={<Forgot />} />
      <Route path="reset/:token" element={<ResetPassword />} />
      <Route path="verify-email/:token" element={<VerifyEmail />} />
      <Route path="checkout/complete" element={<RequireAuth><CheckoutComplete /></RequireAuth>} />
      <Route path="checkout/bundle/:bundleId" element={<RequireAuth><BundleCheckout /></RequireAuth>} />
      <Route path="checkout/:gameId/:tier" element={<RequireAuth><Checkout /></RequireAuth>} />
      <Route path="link/discord/:code" element={<RequireAuth><LinkDiscord /></RequireAuth>} />

      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        <Route path="home" element={<Home />} />
        <Route path="licenses" element={<Licenses />} />
        <Route path="downloads" element={<Downloads />} />
        <Route path="guides" element={<Guides />} />
        <Route path="guides/:slug" element={<GuideView />} />
        <Route path="support" element={<Support />} />
        <Route path="support/:id" element={<TicketView />} />
        <Route path="account" element={<Account />} />

        <Route path="admin" element={<RequireAuth admin><Overview /></RequireAuth>} />
        <Route path="admin/users" element={<RequireAuth admin><Users /></RequireAuth>} />
        <Route path="admin/users/:id" element={<RequireAuth admin><Users /></RequireAuth>} />
        <Route path="admin/keys" element={<RequireAuth admin><Keys /></RequireAuth>} />
        <Route path="admin/tickets" element={<RequireAuth admin><AdminTickets /></RequireAuth>} />
        <Route path="admin/tickets/:id" element={<RequireAuth admin><AdminTickets /></RequireAuth>} />
        <Route path="admin/content" element={<RequireAuth admin><Content /></RequireAuth>} />
        <Route path="admin/releases" element={<RequireAuth admin><Releases /></RequireAuth>} />
        <Route path="admin/games" element={<RequireAuth admin><Games /></RequireAuth>} />
        <Route path="admin/payments" element={<RequireAuth admin><Payments /></RequireAuth>} />
        <Route path="admin/diagnostics" element={<RequireAuth admin><Diagnostics /></RequireAuth>} />
        <Route path="admin/audit" element={<RequireAuth admin><Audit /></RequireAuth>} />
        <Route path="admin/incidents" element={<RequireAuth admin><Incidents /></RequireAuth>} />
        <Route path="admin/settings" element={<RequireAuth admin><Settings /></RequireAuth>} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
    </>
  );
}

installErrorReporting();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
    <BrowserRouter>
      <UIProvider>
        <AuthProvider>
          <LiveProvider>
            <App />
            <CommandPalette />
          </LiveProvider>
        </AuthProvider>
      </UIProvider>
    </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
);
